import { ChecklistEvaluationKey } from './ChecklistEvaluations';
import type { EvaluationCoreDescriptor } from './ChecklistEvaluations';
import { FlagPurposeType } from './Flags';

// The declared computeRow signature uses Record<string, number|...> for all evaluations;
// the actual runtime shape here is string-valued. Casts mirror the pattern in serialValidation.
interface SolarEdgeOptimizerEntered {
  model?: string | null;
  serial?: string | null;
}

// U650/U650B qualify for full domestic content value with any serial number.
const FULL_VALUE_MODELS = new Set(['u650', 'u650b']);

// S500B and S440 qualify for Non-PFE value only when the serial begins with one of these 2-char prefixes.
const NON_PFE_PREFIXES: Record<string, Set<string>> = {
  s500b: new Set(['sb', 'st', 'sv', 'sm', 'sz']),
  s440: new Set(['sb', 'st', 'sv', 'sm', 'sz', 's4']),
};

export const solarEdgeOptimizerCore: EvaluationCoreDescriptor = {
  key: ChecklistEvaluationKey.solarEdgeOptimizerValidation,
  label: 'SolarEdge Optimizer',
  repeatable: false,
  flagPurposeType: FlagPurposeType.rejection,
  supportsDecisionOptions: true,
  defaultParams: {},
  // `inverterModel` is derived-only (never read by computeRow) but still renders as a metric row,
  // so it needs an entry here — its literal name collides with a ChecklistReferenceKey.
  fieldFormats: {
    inverterModel: { label: 'Inverter Model' },
    model: { label: 'Model' },
    serial: { label: 'Serial Number' },
  },
  verdictMessages: {
    pass: 'Model/serial qualifies for domestic content — no flag recommended',
    fail: 'Model/serial does not qualify for domestic content — rejection flag recommended',
    incomplete: 'Data unavailable',
  },
  computeRow: (_derived, entered, _params) => {
    const { model, serial } = entered as unknown as SolarEdgeOptimizerEntered;
    const modelTrimmed = model?.trim();
    const serialTrimmed = serial?.trim();

    if (!modelTrimmed || !serialTrimmed) return { verdict: 'incomplete' };

    const modelKey = modelTrimmed.toLowerCase();

    if (FULL_VALUE_MODELS.has(modelKey)) {
      return { verdict: 'pass', qualificationType: 'full' };
    }

    const allowedPrefixes = NON_PFE_PREFIXES[modelKey];
    if (!allowedPrefixes) return { verdict: 'fail' };

    const prefix = serialTrimmed.slice(0, 2).toLowerCase();
    if (allowedPrefixes.has(prefix)) {
      return { verdict: 'pass', qualificationType: 'nonPfeOnly' };
    }

    return { verdict: 'fail' };
  },
  buildFlagReason: (rows, _params) => {
    const entered = rows[0]?.entered as unknown as SolarEdgeOptimizerEntered | undefined;
    const model = (entered?.model ?? '').trim();
    const serial = (entered?.serial ?? '').trim();
    return `SolarEdge optimizer ${model} (serial ${serial || 'not entered'}) does not qualify for domestic content`;
  },
};
